import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Button } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo } from 'react';
import { UseFieldArrayAppend, useWatch } from 'react-hook-form';
import { uuid } from '../../../../util/uuid';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import {
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
  IMenuSectionOption,
  TemplateSectionFormValues,
} from '../../../../schemas/kpi-template.schema';
import { getAvailableMenuOptions, getDefaultSectionType } from './section-utils';
import { IMenuItem, MenuButton } from '../../../Finance2/common-grid-defs/MenuButton';

const SectionTypeIcon = {
  [CustomTemplateSectionType.quantitative]: <AssessmentIcon fontSize='small' />,
  [CustomTemplateSectionType.textual]: <ArticleIcon fontSize='small' />,
  [CustomTemplateSectionType.file]: <InsertDriveFileIcon fontSize='small' />,
  [CustomTemplateSectionType.date]: <CalendarTodayIcon fontSize='small' />,
} as const;

interface AddSectionMenuProps {
  append: UseFieldArrayAppend<CustomKpiTemplateFormValues, 'sections'>;
}

const EmptySections: TemplateSectionFormValues[] = [];

function useAddSectionMenu(append: AddSectionMenuProps['append']) {
  const { showFileKpiConfig } = useFlags<MaggieFeatureFlags>();
  const sections = useWatch<CustomKpiTemplateFormValues, 'sections'>({ name: 'sections' }) ?? EmptySections;

  const availableOptions = useMemo(
    () =>
      getAvailableMenuOptions(sections as TemplateSectionFormValues[], { showFileOption: showFileKpiConfig }),
    [sections, showFileKpiConfig]
  );

  const handleSelect = useCallback(
    (option: IMenuSectionOption) => {
      const sectionType = getDefaultSectionType(option.type, sections as TemplateSectionFormValues[]);
      append({ _key: uuid(), type: option.type, sectionType, kpis: [] });
    },
    [append, sections]
  );

  const menuItems: IMenuItem[] = useMemo(
    () =>
      availableOptions.map((option) => ({
        name: option.label,
        icon: SectionTypeIcon[option.type],
        onClick: () => handleSelect(option),
      })),
    [availableOptions, handleSelect]
  );

  return menuItems;
}

export function AddSectionMenu({ append }: AddSectionMenuProps) {
  const menuItems = useAddSectionMenu(append);

  return (
    <div>
      <MenuButton
        menuItems={menuItems}
        renderButton={(props) => (
          <Button {...props} startIcon={<AddIcon />} variant='contained' color='secondary' size='small'>
            Add Section
          </Button>
        )}
      />
    </div>
  );
}
